// Runs an inline (no-navigation) admin action as an Inertia visit and resolves
// to its outcome, so optimistic flows and undo stacks can await a definite
// success/failure the way they would a fetch:
//
//   { ok: true, flash }                         the controller redirected back cleanly
//   { ok: false, code, message, errors }        it redirected back with `inertia: { errors: }`
//   { ok: false, networkError: true }           the request never got an Inertia response
//
// Controllers answer with `redirect_back` + flash, and on failure add
// `inertia: { errors: { review: <message>, review_code: <ErrorClass> } }` (a denial
// adds `{ base: <message> }`). `flash` is the page's flash data (`flash.inertia`
// keys included), for actions that hand back a small result such as bulk failures.
//
// Visits are `async` so a burst of reviews never cancels one in flight, and keep
// the page's state and scroll. `only` names the props the redirect refreshes;
// leaving the shared `flash` prop out keeps the global toast quiet.
export const errorMessage = (errors = {}) =>
  errors.review || errors.base || Object.values(errors).find((value) => typeof value === 'string') || 'The action failed.'

export const inertiaAction = (router, method, url, data = {}, { only, ...options } = {}) =>
  new Promise((resolve) => {
    let settled = false
    const settle = (outcome) => {
      if (settled) return
      settled = true
      resolve(outcome)
    }

    router.visit(url, {
      method,
      data,
      async: true,
      preserveState: true,
      preserveScroll: true,
      ...(only ? { only } : {}),
      ...options,
      onSuccess: (page) => {
        const errors = page?.props?.errors || {}
        if (Object.keys(errors).length > 0) {
          settle({ ok: false, code: errors.review_code || null, message: errorMessage(errors), errors })
        } else {
          settle({ ok: true, flash: page?.flash || {} })
        }
      },
      onError: (errors) => settle({ ok: false, code: errors.review_code || null, message: errorMessage(errors), errors }),
      onFinish: () => settle({ ok: false, networkError: true })
    })
  })
